import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, formatApiError } from "../api";
import { useAuth } from "../context/AuthContext";

const ROLE_PL = { EDU_INSTITUTION_ADMIN: "Administrator uczelni", EDU_INSTRUCTOR: "Prowadzący",
  EDU_RESEARCHER: "Badacz", EDU_STUDENT: "Student" };

export default function EduInvitePage() {
  const { token } = useParams();
  const { user } = useAuth();
  const [invite, setInvite] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/edu/invites/${token}`).then((r) => setInvite(r.data)).catch(() => setNotFound(true));
  }, [token]);

  const accept = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`/edu/invites/${token}/accept`, { terms_accepted: terms });
      setDone(r.data);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  if (notFound) return (
    <div className="mx-auto max-w-xl px-4 py-16 text-center" data-testid="edu-invite-invalid">
      <h1 className="text-xl font-bold text-slate-900">Zaproszenie nieprawidłowe lub wygasłe</h1>
      <p className="mt-2 text-sm text-slate-600">Poproś administratora uczelni o nowe zaproszenie.</p>
    </div>);
  if (!invite) return <div className="p-20 text-center text-slate-500" data-testid="edu-invite-loading">Ładowanie…</div>;

  if (done) return (
    <div className="mx-auto max-w-xl px-4 py-16 text-center" data-testid="edu-invite-accepted">
      <Helmet><title>Witaj w PlanGmina EDU</title></Helmet>
      <h1 className="text-2xl font-bold text-slate-900">Dostęp EDU aktywny</h1>
      <p className="mt-3 text-sm text-slate-600">
        {invite.institution_name} · rola: <b>{ROLE_PL[done.edu_role] || done.edu_role}</b>
      </p>
      <Link to="/edu/panel" data-testid="edu-invite-goto-panel"
        className="mt-6 inline-block rounded-lg bg-red-700 px-5 py-3 text-sm font-semibold text-white hover:bg-red-800">
        Przejdź do panelu EDU
      </Link>
    </div>);

  const returnTo = encodeURIComponent(`/edu/zaproszenie/${token}`);
  return (
    <div className="mx-auto max-w-xl px-4 py-10" data-testid="edu-invite-page">
      <Helmet><title>Zaproszenie — PlanGmina EDU</title></Helmet>
      <h1 className="text-2xl font-bold text-slate-900">Zaproszenie do PlanGmina EDU</h1>
      <div className="mt-4 rounded-xl border border-slate-300 p-4 text-sm" data-testid="edu-invite-details">
        <p><span className="text-slate-500">Uczelnia:</span> <b>{invite.institution_name}</b></p>
        <p className="mt-1"><span className="text-slate-500">Rola:</span> <b>{ROLE_PL[invite.edu_role] || invite.edu_role}</b></p>
        {invite.cohort && <p className="mt-1"><span className="text-slate-500">Grupa:</span> {invite.cohort}</p>}
      </div>

      <label className="mt-6 flex items-start gap-3 rounded-xl border-2 border-red-700 bg-red-50 p-4" data-testid="edu-terms-block">
        <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)}
          data-testid="edu-terms-checkbox" className="mt-1" />
        <span className="text-xs text-red-900">{invite.terms_pl}</span>
      </label>
      <p className="mt-2 text-xs text-slate-600" data-testid="edu-invite-legal-links">
        Pełna treść dokumentów:{" "}
        <Link to="/edu/regulamin" className="text-red-700 underline" data-testid="edu-invite-regulamin-link">Regulamin EDU</Link>{" · "}
        <Link to="/edu/prywatnosc" className="text-red-700 underline" data-testid="edu-invite-privacy-link">Informacja o danych osobowych</Link>{" · "}
        <Link to="/edu/retencja" className="text-red-700 underline" data-testid="edu-invite-retention-link">Zasady retencji</Link>
      </p>

      {user === false && (
        <div className="mt-6 rounded-xl border border-slate-300 p-4 text-center" data-testid="edu-invite-login-prompt">
          <p className="text-sm text-slate-700">Zaloguj się lub utwórz konto, aby przyjąć zaproszenie.</p>
          <Link to={`/logowanie?returnTo=${returnTo}`} data-testid="edu-invite-login"
            className="mt-3 inline-block rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
            Zaloguj / zarejestruj
          </Link>
        </div>
      )}
      {user && (
        <>
          {err && <p className="mt-4 text-sm text-red-600" data-testid="edu-invite-error">{err}</p>}
          <button onClick={accept} disabled={!terms || busy} aria-disabled={!terms || busy} data-testid="edu-accept-invite"
            className="mt-6 w-full rounded-lg bg-red-700 px-4 py-3 text-sm font-semibold text-white hover:bg-red-800 disabled:opacity-50 transition-colors">
            {busy ? "Aktywowanie…" : "Akceptuję warunki i dołączam"}
          </button>
        </>
      )}
    </div>
  );
}
